import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { SERVICES } from '../../lib/services-data';
import { adminGet, adminDelete, adminUpload, reportError, AGENCY_ID } from './adminApi';

export function AdminPhotosTab({ adminKey }) {
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ service_id: 'entretien-menager', agency: AGENCY_ID, caption: '', order: 0 });
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    setError('');
    try {
      const res = await adminGet('/api/admin/service-photos', adminKey);
      setPhotos(res.data.photos || []);
    } catch (err) {
      setError(reportError('chargement des photos', err) || 'Erreur de chargement');
    }
  }, [adminKey]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const addPair = async () => {
    if (!before || !after) { toast.error('Les deux photos (avant et après) sont obligatoires'); return; }
    try {
      const fd = new FormData();
      fd.append('service_id', form.service_id);
      fd.append('agency', form.agency);
      fd.append('caption', form.caption);
      fd.append('order', form.order);
      fd.append('before_photo', before);
      fd.append('after_photo', after);
      await adminUpload('post', '/api/admin/service-photos', adminKey, fd);
      toast.success('Photos ajoutées');
      setForm((p) => ({ ...p, caption: '', order: 0 }));
      setBefore(null); setAfter(null);
      fetchPhotos();
    } catch (err) {
      reportError('ajout photos', err, (m) => toast.error(m || "Erreur lors de l'ajout"));
    }
  };

  const remove = async (id) => {
    try {
      await adminDelete(`/api/admin/service-photos/${id}`, adminKey);
      toast.success('Photo supprimée');
      fetchPhotos();
    } catch (err) {
      reportError('suppression photo', err, () => toast.error('Erreur lors de la suppression'));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-photos-tab">
      <Card className="p-5">
        <h3 className="font-['Space_Grotesk'] font-semibold mb-4">Ajouter Avant / Après</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end mb-4">
          <div>
            <label className="text-xs font-medium block mb-1">Agence</label>
            <Select value={form.agency} onValueChange={(v) => setForm((p) => ({ ...p, agency: v }))}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={AGENCY_ID}>Artois.Ptitservices</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Service</label>
            <Select value={form.service_id} onValueChange={(v) => setForm((p) => ({ ...p, service_id: v }))}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{SERVICES.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Légende</label>
            <Input className="h-9" value={form.caption} onChange={(e) => setForm((p) => ({ ...p, caption: e.target.value }))} placeholder="Ex: Ménage complet salon" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Photo AVANT *</label>
            <input type="file" accept="image/*" ref={beforeRef} className="hidden" onChange={(e) => setBefore(e.target.files?.[0] || null)} />
            <Button variant="outline" size="sm" className="w-full h-9" onClick={() => beforeRef.current?.click()}>
              <Camera className="h-3.5 w-3.5 mr-1" /> {before ? 'Avant OK' : 'Choisir'}
            </Button>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Photo APRÈS *</label>
            <input type="file" accept="image/*" ref={afterRef} className="hidden" onChange={(e) => setAfter(e.target.files?.[0] || null)} />
            <Button variant="outline" size="sm" className="w-full h-9" onClick={() => afterRef.current?.click()}>
              <Camera className="h-3.5 w-3.5 mr-1" /> {after ? 'Après OK' : 'Choisir'}
            </Button>
          </div>
        </div>
        <Button size="sm" onClick={addPair}>Ajouter la paire de photos</Button>
      </Card>

      {error && <Card className="p-4 text-sm text-destructive">{error}</Card>}
      {!error && photos.length === 0 ? (
        <Card className="p-8 text-center"><p className="text-muted-foreground">Aucune photo avant/après. Ajoutez-en ci-dessus.</p></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {photos.map((p) => (
            <Card key={p._id} className="p-3">
              <div className="flex items-center justify-between mb-2">
                <Badge variant="secondary" className="text-xs">{p.service_id}</Badge>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7" onClick={() => remove(p._id)} aria-label="Supprimer"><XCircle className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="grid grid-cols-2 gap-1 rounded-lg overflow-hidden">
                <div className="relative">
                  <span className="absolute top-1 left-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">Avant</span>
                  <img src={p.before_photo} alt="Avant" className="w-full h-24 object-cover" />
                </div>
                <div className="relative">
                  <span className="absolute top-1 right-1 bg-[hsl(var(--primary))]/80 text-white text-[10px] px-1.5 py-0.5 rounded">Après</span>
                  <img src={p.after_photo} alt="Après" className="w-full h-24 object-cover" />
                </div>
              </div>
              {p.caption && <p className="text-xs text-muted-foreground mt-2">{p.caption}</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
